package com.macrofocus.eflux

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.properties.PropertyListener
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.common.selection.SimpleSelection
import com.macrofocus.common.selection.SimpleSingleSelection
import com.macrofocus.eflux.context.ActiveTooltip
import com.macrofocus.eflux.network.LinkField
import com.macrofocus.eflux.network.NodeField
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.network.logical.LogicalNetwork
import com.macrofocus.eflux.network.physical.PhysicalNetwork
import com.macrofocus.eflux.time.ResettingTimeTracker
import com.macrofocus.eflux.time.TimeTracker
import korlibs.time.days
import korlibs.time.hours
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import org.mkui.interaction.InteractionMode
import org.molap.dataframe.DataFrame
import kotlin.js.Promise

class EFluxConfiguration(val earliest: Promise<String>, val latest: Promise<String>, val timeTracker: TimeTracker = ResettingTimeTracker("20230101_0000".toDateTime(), 30.days, 24.hours)) {
    val expertMode = SimpleProperty(false)

    /*
        We make the distinction between the following two key dates and one duration:
        display: the date shown in the date chooser and used to display the data
        max: the date for which there is no lag
        lag: the (negative) amount of time we are looking in the past

        max = display - lag
        lag -> max = display - lag

        earliest: the earliest date stored on the server
        latest: the latest date stored on the server

        If live synchronization on:
        latest -> display = latest + lag -> display - lag -> max
//        if(display + lag) > latest -> lag =
        new = false

        If date is manually inputted:
        display -> lag = display - max

        If live synchronization off:
        new = latest > max

        Switching synchronization on:
        live -> latest -> display
     */

//    val masterDate = SimpleProperty("20230101_0000".toRawDate())
//    val masterDateTime: MutableProperty<DateTime> = NewTransformedProperty(masterDate, { it.toDateTime() }, { it.toJsDate() })
    val displayDateTime = timeTracker.displayDateTime
//    val displayDate =  OTransformedProperty(displayDateTime, { it.toJsDate() }, { it.toDateTime() })
    val masterDateTime = timeTracker.masterDateTime
//    val masterDate =  OTransformedProperty(masterDateTime, { it.toJsDate() }, { it.toDateTime() })

    val lag = timeTracker.masterLagProperty
    val lagUnit = timeTracker.lagUnitProperty

    val maxDateTime = timeTracker.maxDateTime

    val latestDateTime = timeTracker.latestDateTime

    val nowDate = SimpleProperty("Waiting for time")
    val live = timeTracker.liveProperty

    val physicalNetwork: MutableProperty<PhysicalNetwork?> = SimpleProperty(null)
    val logicalNetwork: MutableProperty<LogicalNetwork?> = SimpleProperty(null)
    val coordinatesDataFrame: MutableProperty<DataFrame<Int, String, Any?>?> = SimpleProperty(null)
    var itcmDataFrame: MutableProperty<DataFrame<Int, String, Any?>?> = SimpleProperty(null)
    var amicaDataFrame: MutableProperty<DataFrame<Int, String, Any?>?> = SimpleProperty(null)

    val sizeNodes: MutableProperty<NodeField<*>?> = SimpleProperty(null)
    val colorNodes: MutableProperty<NodeField<*>?> = SimpleProperty(NodeField.v2)

    val tooltipNodes: MutableProperty<Array<NodeField<*>>> = SimpleProperty(NodeField.selected)
    val tooltipLinks: MutableProperty<Array<LinkField<*>>> =
        SimpleProperty(LinkField.selected)

    val heatmapNodes: MutableProperty<NodeField<*>?> = SimpleProperty(null)

    val thicknessLinks: MutableProperty<LinkField<*>?> = SimpleProperty(null)
    val colorLinks: MutableProperty<LinkField<*>?> = SimpleProperty(LinkField.baskv)
    val movementLinks: MutableProperty<LinkField<*>?> = SimpleProperty(LinkField.p)
    val animationLinks: MutableProperty<Boolean> = SimpleProperty(true)
    val heatmapLinks: MutableProperty<LinkField<*>?> = SimpleProperty(null)

    val logicalBusProbing: MutableSingleSelection<LogicalBusId> = SimpleSingleSelection()
    val logicalBranchProbing: MutableSingleSelection<LogicalBranchId> = SimpleSingleSelection()

    val logicalBusSelection: MutableSelection<LogicalBusId> = SimpleSelection()
    val logicalBranchSelection: MutableSelection<LogicalBranchId> = SimpleSelection()

//    val nodeSelection: MutableSelection<CombinedBuses> = SimpleSelection()
//    val linkSelection: MutableSelection<CombinedBranches> = SimpleSelection()

    val cities: MutableProperty<Boolean> = SimpleProperty(false)
    val labelSubstations: MutableProperty<Boolean> = SimpleProperty(true)
    val border: MutableProperty<Boolean> = SimpleProperty(true)
    val legend: MutableProperty<Boolean> = SimpleProperty(false)
    val dnd: MutableProperty<Boolean> = SimpleProperty(false)
    val activeTooltips: MutableProperty<ActiveTooltip> = SimpleProperty(ActiveTooltip.Both)
    val tooltips: MutableProperty<Boolean> = SimpleProperty(false)
    val lod: MutableProperty<Boolean> = SimpleProperty(false)
    val generators: MutableProperty<Boolean> = SimpleProperty(false)
    val lightning: MutableProperty<Boolean> = SimpleProperty(false)
    val weather: MutableProperty<Boolean> = SimpleProperty(false)
    val weatherVariable: MutableProperty<String> = SimpleProperty("t_70m")
    val terrain: MutableProperty<Boolean> = SimpleProperty(false)

    val download: MutableProperty<Boolean> = SimpleProperty(false)

    val viewStyle: MutableProperty<ViewStyle> = SimpleProperty(ViewStyle.COMBINED)

    val interactionMode: MutableProperty<InteractionMode> = SimpleProperty(InteractionMode.Selection)

    val feedbackMode = SimpleProperty("link")
    val uiscale = SimpleProperty(1.0)

    var tableType = SimpleProperty(TableType.CROSSBORDER)
    val crossBorder = SimpleProperty(false)
    val zoneFilter = SimpleProperty("")
    var filter = SimpleProperty("")

    init {
        GlobalScope.async {
            // Initialize date
            timeTracker.setEarliestLatest(earliest.await().toDateTime(), latest.await().toDateTime())
        }

        nowDate.addPropertyListener(object : PropertyListener<String> {
            override fun propertyChanged(event: PropertyEvent<String>) {
                timeTracker.newDataReceived(event.newValue.toDateTime())
            }
        })

        val configuration = this
        viewStyle.addPropertyListener(object : PropertyListener<ViewStyle> {
            override fun propertyChanged(event: PropertyEvent<ViewStyle>) {
                event.newValue.apply(configuration)
            }
        })
        viewStyle.value.apply(configuration)
    }
}